/**
 * The home chain (1.I-1 deliverable 7): the open document's title preceded by its homes, root
 * first. The walk follows `homeId` through `docs.meta` and stops at a null `homeId`, an id it
 * has already seen, a failed read, or a parent that is not live. It reads each id once.
 */

export interface ChainMeta {
  readonly id: string;
  readonly title: string;
  readonly homeId: string | null;
  readonly deletedAt: string | null;
  readonly mergedIntoId: string | null;
}

export interface Crumb {
  readonly id: string;
  readonly title: string;
}

export const MAX_CHAIN_HOPS = 8;

function isLive(meta: ChainMeta): boolean {
  return meta.deletedAt === null && meta.mergedIntoId === null;
}

export async function walkHomeChain(
  id: string,
  getMeta: (id: string) => Promise<ChainMeta>,
  options: { readonly maxHops?: number } = {},
): Promise<Crumb[]> {
  const maxHops = options.maxHops ?? MAX_CHAIN_HOPS;
  let current: ChainMeta;
  try {
    current = await getMeta(id);
  } catch {
    return [];
  }
  const seen = new Set<string>([id]);
  const chain: Crumb[] = [{ id, title: current.title }];
  for (let hops = 0; hops < maxHops && current.homeId !== null; hops++) {
    const parentId = current.homeId;
    if (seen.has(parentId)) break;
    seen.add(parentId);
    let parent: ChainMeta;
    try {
      parent = await getMeta(parentId);
    } catch {
      break;
    }
    if (!isLive(parent)) break;
    chain.unshift({ id: parentId, title: parent.title });
    current = parent;
  }
  return chain;
}

/** `document.title` for a last crumb: "<crumb> — Campaign Codex", or the app's name alone. */
export function pageTitle(lastCrumb: string | null): string {
  return lastCrumb ? `${lastCrumb} — Campaign Codex` : 'Campaign Codex';
}
